import * as IoC from 'typescript-ioc';
import { action, observable } from 'mobx';
import { ICreateEventBlueprintDTO, IEventBlueprintDTO, IMeetingTemplateDTO } from '../../../../api/blueprints/blueprints.dao';
import { configList, FieldKey } from './event-blueprint-form.config';
import { FormattedMessage, IntlShape } from 'react-intl';
import { EventBlueprintMapper, EventBlueprintValues, ParticipantLinkType } from './event-blueprint.mapper';
import {
    BLUEPRINTS_TRACKING_EVENTS,
    convertBoolTypeEmptyForMixPanel,
    convertBoolTypeForMixPanel,
    isTrackingFieldEnabled
} from '../../../../tracking-service/tracking-values';
import { NONE } from '../../../../constants/common.constants';
import React from 'react';
import { IAlertsService } from '../../../../infrastructure/alerts';
import { Space } from '../../../../constants/html-character-entities';
import { S3ObjectMetadata } from '../../../../api/shared.dto';
import { TEventTypeLabel } from '../../../../api/events.dao';
import { AttendeeLinkType } from '../../../../api/attendees.dao';
import { attendeeLinkTypeOptions } from '../../../../constants/blueprint-labels.constants';
import { BaseBlueprintFormVM } from '../../blueprint-form-base.viewmodel';
import { TObject } from '../../../../infrastructure/type-extensions';
import { PASSPORT_URL_YEAR_FORMATS } from '../../../../api/blueprints/blueprint.types';
import { IBlueprintItemDetails } from '../../../../api/blueprints/blueprint-details.types';
import { convertBlueprintTemplateIntoBlueprintDetails } from '../../../../infrastructure/utils/blueprint-details.mapper';
import { IAttributesDAO, IEventAttributeDetailsDTO, TGetEventAttributesSortKeys } from '../../../../api/attributes.dao';
import { ISortRequestParams, SortOrder } from '../../../../api/shared.dto';
import { hasPermission } from '../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../api/roles.dao';
import { ISubWindowFacade, SubWindowStatus } from '../../../../infrastructure/sub-windows';
import { OrderBookingEmailsList } from './order-booking-emails/order-booking-emails-list';
import { OrderBookingEmailsListVM } from './order-booking-emails/order-booking-emails-list.viewmodel';
import { CONFIGURE_ORDER_BOOKING_EMAILS_TITLE } from './order-booking-emails/order-booking-emails.texts';

export class EventBlueprintFormVM extends BaseBlueprintFormVM {
    @observable
    public meetingTemplates: IMeetingTemplateDTO[] = [];

    @observable
    private blueprintEventTypes: TEventTypeLabel[] = [];

    @observable
    private eventAttributes: IEventAttributeDetailsDTO[] = [];

    @observable
    private orderBookingEmailsPopup: ISubWindowFacade;

    constructor(
        private readonly accountId: number,
        private readonly intl: IntlShape,
        private readonly apiCallSuccessCallback: () => void,
        private readonly deleteCompletionCallback: () => void,
        private readonly eventTypesUsedInAccount: TEventTypeLabel[] = [],
        blueprintId?: string,
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly attributesDAO: IAttributesDAO = IoC.Container.get(IAttributesDAO),
    ) {
        super(blueprintId);
    }

    @action
    public async initFormVM(): Promise<void> {
        if (this.blueprintId) {
            const result = await this.getBluePrint(this.accountId) as IEventBlueprintDTO;

            this.setBlueprintEventTypes(result.eventTypes);
            this.setBlueprintSnapshotFromDto(result);
        }

        await this.initMeetingTemplates();
        if (hasPermission(Permission.VIEW_EVENT_ATTRIBUTES)) {
                await this.loadEventAttributes();
        }
        this.initializeFormData(configList, this.getBlueprintSnapshot());
        this.checkDefaultMeetingTemplate();
    }

    @action.bound
    public async createEventBluePrint(): Promise<boolean> {
        this.startLoading();

        try {
            if (this.hasPassportConfig()) await this.storeCurrentPassportConfig();

            const dto = EventBlueprintMapper.toEventBlueprintDTO(this.composeEventBlueprintValues());

            await this.blueprintsDAO.createBlueprint<ICreateEventBlueprintDTO, IEventBlueprintDTO>(dto);
            this.trackSubmitBlueprintFormMixPanel();
            this.apiCallSuccessCallback();

            return true;
        } catch (error) {
            EventBlueprintFormVM.handleApiException(error);
        } finally {
            this.stopLoading();
        }
    }

    @action.bound
    public async editEventBluePrint(): Promise<boolean> {
        this.startLoading();

        try {
            await this.callBlueprintUpdateMethod();
            this.trackSubmitBlueprintFormMixPanel();
            this.apiCallSuccessCallback();

            return true;
        } catch (error) {
            EventBlueprintFormVM.handleApiException(error);
        } finally {
            this.stopLoading();
        }
    }

    public submitBluePrint = (): Promise<boolean> => {
        if (!this.formDataCtrl.isVmValid()) {
            this.formDataCtrl.setVmTouched();
        } else {
            return this.callProperSubmitAction();
        }
    };

    public trackSubmitBlueprintFormMixPanel = (): void => {
        const eventType = this.isEditMode ? BLUEPRINTS_TRACKING_EVENTS.EDIT_EVENT_BLUEPRINT : BLUEPRINTS_TRACKING_EVENTS.NEW_EVENT_BLUEPRINT;

        this.trackingService.get().track(eventType, {
            'Account ID': this.accountId,
            'Account Name': this.globalStorage.getAccount()?.name,
            'Blueprint Name': this.buildEditableFieldCheckMixPanel(FieldKey.NAME),
            'Blueprint ID': this.blueprintId || null,
            'Blueprint Description': convertBoolTypeEmptyForMixPanel(!!this.formDataCtrl.getValue(FieldKey.DESCRIPTION)),
            'Time Zone': this.buildEditableFieldCheckMixPanel(FieldKey.TIME_ZONE),
            'Region': this.buildEditableFieldCheckMixPanel(FieldKey.REGION),
            'Business Line': this.buildEditableFieldCheckMixPanel(FieldKey.BUSINESS_LINE),
            'Event Type': this.buildEditableFieldCheckMixPanel(FieldKey.TYPE),
            'Access': this.buildEditableFieldCheckMixPanel(FieldKey.EVENT_ACCESS),
            'Default Meeting Duration': this.buildEditableFieldCheckMixPanel(FieldKey.DEFAULT_MEETING_DURATION),
            'Integration': this.booleanFieldToTrackingValue(FieldKey.INTEGRATION_ENABLED),
            'Attendee Link': this.attendeeLinkToTrackingValue(),
            'Event Types': this.eventTypesToTrackingValue(),
            'Default Meeting Template': this.defaultMeetingTemplateToTrackingValue(),
        });
    };

    public deleteBluePrint = (): void => {
        this.alertsService.confirm({
            message: (
                <span className='alert-message'>
                    <FormattedMessage
                        id='oe.confirm.areYouSureTo'
                        description='Confirm message'
                        defaultMessage='Are you sure you want to'
                    />
                    <Space />
                    <span className='alert-message--danger'>
                        <FormattedMessage
                            id='oe.blueprints.deleteBluePrint'
                            description='Confirm message'
                            defaultMessage='Delete this Blueprint?'
                        />
                    </span>
                    <Space />
                    <FormattedMessage
                        id='oe.blueprints.thisCannotBeUndoneAllEventsBlueprintWillLose'
                        description='Alert message'
                        defaultMessage='This cannot be undone. All Events previously created using this Blueprint will lose
                         their association with it, and no further automations will take place.'
                    />
                </span>
            ),
            confirmButtonProps: {
                text: <FormattedMessage
                    description='Label text'
                    defaultMessage='Yes, Delete'
                    id='oe.blueprint.YesDelete'
                />,
                modifier: 'confirm-warn',
                handler: this.deleteBlueprintApiCall,
            },
            cancelButtonProps: {
                modifier: 'cancel'
            }
        });
    };

    public deleteBlueprintApiCall = async (): Promise<void> => {
        try {
            await this.blueprintsDAO.deleteBlueprint({
                accountId: this.accountId,
                blueprintId: this.blueprintId
            });

            if (this.hasStoredPassportConfig()) await this.deleteStoredPassportConfig();

            this.trackDeleteBluePrintMixPanel();
            this.deleteCompletionCallback();
        } catch (error) {
            EventBlueprintFormVM.handleApiException(error);
        }
    };

    public trackDeleteBluePrintMixPanel = (): void => {
        this.trackingService.get().track(BLUEPRINTS_TRACKING_EVENTS.DELETE_EVENT_BLUEPRINT, {
            'Account Name': this.globalStorage.getAccount()?.name,
            'Account ID': this.accountId,
            'Blueprint Name': this.formDataCtrl.getValue(FieldKey.NAME),
            'Blueprint ID': this.blueprintId || null,
        });
    };

    @action
    public enableCreateSingleMeeting(): void {
        this.formDataCtrl.setValue(FieldKey.CREATE_SINGLE_MEETING, true);
    }

    @action
    public disableCreateSingleMeeting(): void {
        this.formDataCtrl.setValue(FieldKey.CREATE_SINGLE_MEETING, false);
        this.formDataCtrl.setValue(FieldKey.MEETING_TEMPLATE, '');
    }

    @action
    public enableCreatePassportSite(): void {
        this.formDataCtrl.setValue(FieldKey.CREATE_PASSPORT_SITE, true);
        this.formDataCtrl.setValue(FieldKey.PASSPORT_URL_FORMAT, PASSPORT_URL_YEAR_FORMATS.SHORT_YEAR_FORMAT);
    };

    @action
    public disableCreatePassportSite(): void {
        this.formDataCtrl.setValue(FieldKey.CREATE_PASSPORT_SITE, false);
        this.deletePassportConfig();
        this.formDataCtrl.setValue(FieldKey.PASSPORT_URL_FORMAT, null);
    };

    @action.bound
    public async uploadPassportConfig(file: File): Promise<void> {
        try {
            const { objectKey } = await this.blueprintsDAO.uploadFileToTempStorage(file, { accountId: this.accountId });
            this.setPassportConfig({ objectKey: objectKey, title: file.name });
        } catch (error) {
            EventBlueprintFormVM.handleApiException(error);
        }
    }

    @action.bound
    public deletePassportConfig(): void {
        this.formDataCtrl.setValue(FieldKey.PASSPORT_CONFIG, null);
    }

    @action.bound
    public setPassportConfig(config: S3ObjectMetadata): void {
        this.formDataCtrl.setValue(FieldKey.PASSPORT_CONFIG, config);
    }

    public getPassportConfig(): S3ObjectMetadata | null {
        return this.formDataCtrl.getValue(FieldKey.PASSPORT_CONFIG) as S3ObjectMetadata | null;
    }

    public hasPassportConfig(): boolean {
        return !!this.getPassportConfig();
    }

    @action
    public enableIntegration(): void {
        this.formDataCtrl.setValue(FieldKey.INTEGRATION_ENABLED, true);
        this.formDataCtrl.setValue(FieldKey.ATTENDEE_LINK_TYPE, AttendeeLinkType.MEETING_LINK_WITH_EMBEDDED_PASSCODE);
    }
    @action
    public disableIntegration(): void {
        this.formDataCtrl.setValue(FieldKey.INTEGRATION_ENABLED, false);
        this.blueprintEventTypes = [];
        this.formDataCtrl.setValue(FieldKey.ATTENDEE_LINK_TYPE, null);
        this.formDataCtrl.setValue(FieldKey.INTEGRATION_MEETING_TEMPLATE, '');
        this.formDataCtrl.setValue(FieldKey.EXTERNAL_ID, '');
    }

    @action.bound
    public setBlueprintEventTypes(eventTypes: TEventTypeLabel[]): void {
        this.blueprintEventTypes = eventTypes;
    }

    @action.bound
    public removeEventType(eventType: TEventTypeLabel): void {
        const idx = this.blueprintEventTypes.indexOf(eventType);
        this.blueprintEventTypes.splice(idx, 1);
    }

    public getBlueprintEventTypes(): TEventTypeLabel[] {
        return this.blueprintEventTypes;
    }

    public getExcludedEventTypes(): TEventTypeLabel[] {
        return this.eventTypesUsedInAccount;
    }

    public getSelectedMeetingTemplateDetails(meetingTemplateId: string): IBlueprintItemDetails | null {
        const selectedMeeting = this.getSelectedTemplate(meetingTemplateId);
        return selectedMeeting ? convertBlueprintTemplateIntoBlueprintDetails(selectedMeeting.type, selectedMeeting) : null;
    }

    public getSelectedTemplate(meetingTemplateId: string): IMeetingTemplateDTO | undefined {
        return this.meetingTemplates?.find(t => t.blueprintId === meetingTemplateId);
    }

    public toggleDefaultMeetingBlueprint(currentValue: boolean): void {
        currentValue ? this.disableDefaultMeetingBlueprint() : this.enableDefaultMeetingBlueprint();
    }

    public selectDefaultMeetingBlueprint(id: string): void {
        this.formDataCtrl.setValue(FieldKey.DEFAULT_MEETING_BLUEPRINT_ID, id);
    }

    @action
    public enableOrderBooking(): void {
        this.formDataCtrl.setValue(FieldKey.ORDER_BOOKING_ENABLED, true);
        this.formDataCtrl.setValue(FieldKey.PARTICIPANT_LINK_TYPE, ParticipantLinkType.PASSPORT_LINK);
    }

    @action
    public disableOrderBooking(): void {
        this.formDataCtrl.setValue(FieldKey.ORDER_BOOKING_ENABLED, false);
        this.formDataCtrl.setValue(FieldKey.PUBLIC_NAME, '');
        this.formDataCtrl.setValue(FieldKey.PUBLIC_DESCRIPTION, '');
        this.formDataCtrl.setValue(FieldKey.PARTICIPANT_LINK_TYPE, ParticipantLinkType.PASSPORT_LINK);
        this.formDataCtrl.setValue(FieldKey.EVENT_ATTRIBUTE_IDS, []);
    }

    @action
    public async loadEventAttributes(): Promise<void> {
        try {
            const requestParams: { accountId: number } & ISortRequestParams<TGetEventAttributesSortKeys> = {
                accountId: this.accountId,
                sortField: 'createdAt',
                sortOrder: SortOrder.Desc
            };
            const response = await this.attributesDAO.getEventAttributesList(requestParams);
            this.eventAttributes = response.content.filter(item => item.active);
        } catch (error) {
            EventBlueprintFormVM.handleApiException(error);
        }
    }

    @action.bound
    public setEventAttributes(eventAttributes: IEventAttributeDetailsDTO[]): void {
        const eventAttributeIds = eventAttributes.map(attr => attr.eventAttributeId);
        this.formDataCtrl.setValue(FieldKey.EVENT_ATTRIBUTE_IDS, eventAttributeIds);
    }

    @action.bound
    public removeEventAttribute(eventAttribute: IEventAttributeDetailsDTO): void {
        const currentIds = this.formDataCtrl.getValue(FieldKey.EVENT_ATTRIBUTE_IDS) as number[] || [];
        const updatedIds = currentIds.filter(id => id !== eventAttribute.eventAttributeId);
        this.formDataCtrl.setValue(FieldKey.EVENT_ATTRIBUTE_IDS, updatedIds);
    }

    public getSelectedEventAttributes(): IEventAttributeDetailsDTO[] {
        const eventAttributeIds = this.formDataCtrl.getValue(FieldKey.EVENT_ATTRIBUTE_IDS) as number[] || [];
        if (!Array.isArray(eventAttributeIds) || eventAttributeIds.length === 0) {
            return [];
        }

        const attributes = this.eventAttributes || [];
        return attributes.filter(attr => eventAttributeIds.includes(attr.eventAttributeId));
    }

    public getAvailableEventAttributes(): IEventAttributeDetailsDTO[] {
        return this.eventAttributes || [];
    }

    private disableDefaultMeetingBlueprint(): void {
        this.formDataCtrl.setValue(FieldKey.DEFAULT_MEETING_BLUEPRINT_ENABLED, false);
        this.selectDefaultMeetingBlueprint('');
    }

    private enableDefaultMeetingBlueprint(): void {
        this.formDataCtrl.setValue(FieldKey.DEFAULT_MEETING_BLUEPRINT_ENABLED, true);
    }

    private getStoredPassportConfig(): S3ObjectMetadata | null {
        return (this.getBlueprintSnapshot()?.passportConfig ?? null) as S3ObjectMetadata | null;
    }

    private hasStoredPassportConfig(): boolean {
        return !!this.getStoredPassportConfig();
    }

    private async callBlueprintUpdateMethod(): Promise<void> {
        if (this.shouldStorePassportConfig()) {
            await this.storePassportConfigAndUpdateBlueprint();
        } else if (this.shouldDeleteStoredPassportConfig()) {
            await this.deleteStoredPassportConfigAndUpdateBlueprint();
        } else if (this.shouldReplacePassportConfig()) {
            await this.replacePassportConfigAndUpdateBlueprint();
        } else {
            await this.updateEventBlueprint();
        }
    }

    private shouldStorePassportConfig(): boolean {
        return !this.hasStoredPassportConfig() && this.hasPassportConfig();
    }

    @action.bound
    private async storePassportConfigAndUpdateBlueprint(): Promise<void> {
        await this.storeCurrentPassportConfig();
        await this.updateEventBlueprint();
    }

    private shouldDeleteStoredPassportConfig(): boolean {
        return this.hasStoredPassportConfig() && !this.hasPassportConfig();
    }

    @action.bound
    private async deleteStoredPassportConfigAndUpdateBlueprint(): Promise<void> {
        await this.updateEventBlueprint();
        await this.deleteStoredPassportConfig();
    }

    private shouldReplacePassportConfig(): boolean {
        return (
            this.hasStoredPassportConfig()
            && this.hasPassportConfig()
            && this.getStoredPassportConfig().objectKey !== this.getPassportConfig().objectKey
        );
    }

    @action.bound
    private async replacePassportConfigAndUpdateBlueprint(): Promise<void> {
        await this.storeCurrentPassportConfig();
        await this.updateEventBlueprint();
        await this.deleteStoredPassportConfig();
    }

    private async updateEventBlueprint(): Promise<void> {
        const additionalValues = { blueprintId: this.blueprintId, createdAt: this.getBlueprintSnapshot().createdAt };
        const dto = EventBlueprintMapper.toEditEventBlueprintDTO(this.composeEventBlueprintValues(additionalValues));
        await this.blueprintsDAO.editBlueprint<IEventBlueprintDTO>(dto);
    }

    @action.bound
    private async storeCurrentPassportConfig(): Promise<void> {
        const storedConfig = await this.blueprintsDAO.storeFileToStorage({ accountId: this.accountId, ...this.getPassportConfig() });
        this.setPassportConfig(storedConfig);
    }

    @action.bound
    private async deleteStoredPassportConfig(): Promise<void> {
        try {
            await this.blueprintsDAO.deleteFileFromStorage({ accountId: this.accountId, objectKey: this.getStoredPassportConfig().objectKey });
        } catch (error) {
            EventBlueprintFormVM.handleApiException(error);
        }
    }

    @action
    private async initMeetingTemplates(): Promise<void> {
        try {
            this.meetingTemplates = await this.blueprintsDAO.getMeetingTemplateList({ accountId: this.accountId });
        } catch (error) {
            EventBlueprintFormVM.handleApiException(error);
        }
    }

    private callProperSubmitAction = (): Promise<boolean> => {
        return this.isEditMode
            ? this.editEventBluePrint()
            : this.createEventBluePrint();
    };

    @action
    private setBlueprintSnapshotFromDto(dto: IEventBlueprintDTO): void {
        this.blueprintItemSnapshot = EventBlueprintMapper.toEventBlueprintValuesFromEventBlueprintDTO(dto);
        this.blueprintItemSnapshot.createdAt = dto.createdAt;
    }

    private getBlueprintSnapshot(): EventBlueprintValues {
        return this.blueprintItemSnapshot as EventBlueprintValues;
    }

    private composeEventBlueprintValues(additionalValues?: TObject<string>): EventBlueprintValues {
        const values = {
            accountId: this.accountId,
            eventTypes: this.getBlueprintEventTypes(),
            ...additionalValues
        } as EventBlueprintValues;

        this.formDataCtrl.getVmState().forEach(({ name, value }) => {
            values[name] = value;
        });

        const orderBookingEnabled = this.formDataCtrl.getValue(FieldKey.ORDER_BOOKING_ENABLED) as boolean;
        if (orderBookingEnabled) {
            const eventAttributeIds = this.formDataCtrl.getValue(FieldKey.EVENT_ATTRIBUTE_IDS);

            let sanitizedIds: number[] = [];
            if (Array.isArray(eventAttributeIds)) {
                sanitizedIds = eventAttributeIds
                    .map(id => Number(id))
                    .filter(id => !isNaN(id));
            }

            values[FieldKey.EVENT_ATTRIBUTE_IDS] = sanitizedIds;
            values.eventAttributeIds = sanitizedIds;
        }

        return values;
    }

    private booleanFieldToTrackingValue(key: FieldKey): string {
        const currentValue = this.formDataCtrl.getValue(key) as boolean;

        return !this.isEditMode
            ? isTrackingFieldEnabled(currentValue)
            : `${convertBoolTypeForMixPanel(currentValue !== this.getBlueprintSnapshot()[key])} - ${isTrackingFieldEnabled(currentValue)}`;
    }

    private eventTypesToTrackingValue(): string {
        const eventTypes = this.getBlueprintEventTypes();
        const eventTypesString = this.getBlueprintEventTypes().join(', ');

        if (!this.isEditMode) {
            return eventTypesString || NONE;
        } else {
            const snapshotEventTypes = this.getBlueprintSnapshot().eventTypes;

            const isChanged = (
                eventTypes.length !== snapshotEventTypes.length ||
                eventTypes.some(type => !snapshotEventTypes.includes(type))
            );

            return `${convertBoolTypeForMixPanel(isChanged)} - ${eventTypesString || NONE}`;
        }
    }

    private attendeeLinkToTrackingValue(): string {
        const currentValue = this.formDataCtrl.getValue(FieldKey.ATTENDEE_LINK_TYPE);
        const currentLabel = currentValue
            ? attendeeLinkTypeOptions(this.intl).find(linkType => linkType.value === currentValue).label
            : NONE;

        if (!this.isEditMode) {
            return currentLabel;
        } else {
            const isChanged = currentValue !== this.getBlueprintSnapshot()[FieldKey.ATTENDEE_LINK_TYPE];
            return `${convertBoolTypeForMixPanel(isChanged)} - ${currentLabel}`;
        }
    }

    @action
    private checkDefaultMeetingTemplate(): void {
        const defaultMeetingTemplate = this.formDataCtrl.getValue(FieldKey.DEFAULT_MEETING_BLUEPRINT_ID);
        const isMeetingTemplateExist = this.meetingTemplates.find(t => t.blueprintId === defaultMeetingTemplate);
        if (isMeetingTemplateExist) {
            this.formDataCtrl.setValue(FieldKey.DEFAULT_MEETING_BLUEPRINT_ENABLED, true);
        } else {
            this.formDataCtrl.setValue(FieldKey.DEFAULT_MEETING_BLUEPRINT_ENABLED, false);
            this.formDataCtrl.setValue(FieldKey.DEFAULT_MEETING_BLUEPRINT_ID, '');
        }
    }

    private defaultMeetingTemplateToTrackingValue(): string {
        const currentValue = this.formDataCtrl.getValue(FieldKey.DEFAULT_MEETING_BLUEPRINT_ID);
        const defaultMeetingTemplateId = this.meetingTemplates.find(tpl => tpl.blueprintId === currentValue);

        if (!this.isEditMode) {
            return defaultMeetingTemplateId
                ? `Enabled - ${defaultMeetingTemplateId.name}`
                : 'Disabled'
                ;
        } else {
            const valueFromSnapshot = this.getBlueprintSnapshot()[FieldKey.DEFAULT_MEETING_BLUEPRINT_ID] || '';
            const isChanged = currentValue !== valueFromSnapshot;
            const label = defaultMeetingTemplateId
                ? defaultMeetingTemplateId.name
                : 'Disabled'
                ;
            return `${convertBoolTypeForMixPanel(isChanged)} - ${label}`;
        }
    }

    public showOrderBookingEmailsForm = () => {
        if (!this.isEditMode || !this.blueprintId) {
            return;
        }
        if (
            !this.orderBookingEmailsPopup
            || this.orderBookingEmailsPopup.getStatus() === SubWindowStatus.Closed
        ) {
            const listVm = new OrderBookingEmailsListVM(
                this.accountId,
                this.blueprintId,
                this.intl,
            );
            this.orderBookingEmailsPopup = this.subWindowService.showSubWindow({
                componentToHost: OrderBookingEmailsList,
                componentToHostProps: { vm: listVm },
                className: 'email-templates',
                title: this.intl.formatMessage(CONFIGURE_ORDER_BOOKING_EMAILS_TITLE),
            });
        }
    };

}
